// SubtitleContext: Handles subtitle state and controls
'use client'
import { type ReactNode, createContext, useContext, useState } from 'react'

interface SubtitleState {
  enabled: boolean
  language: 'english' | 'maori'
  currentSubtitle: string | null
  subtitleFile: string | null
}

interface SubtitleContextType extends SubtitleState {
  setEnabled: (enabled: boolean) => void
  setLanguage: (lang: 'english' | 'maori') => void
  setCurrentSubtitle: (subtitle: string | null) => void
  setSubtitleFile: (file: string | null) => void
}

const initialState: SubtitleState = {
  enabled: true,
  language: 'english',
  currentSubtitle: null,
  subtitleFile: null,
}

const SubtitleContext = createContext<SubtitleContextType | undefined>(undefined)

export function SubtitleProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SubtitleState>(initialState)

  const setEnabled = (enabled: boolean) => setState((prev) => ({ ...prev, enabled }))
  const setLanguage = (language: 'english' | 'maori') => setState((prev) => ({ ...prev, language }))
  const setCurrentSubtitle = (currentSubtitle: string | null) => setState((prev) => ({ ...prev, currentSubtitle }))
  const setSubtitleFile = (subtitleFile: string | null) => setState((prev) => ({ ...prev, subtitleFile }))

  const value = { ...state, setEnabled, setLanguage, setCurrentSubtitle, setSubtitleFile }
  return <SubtitleContext.Provider value={value}>{children}</SubtitleContext.Provider>
}

export function useSubtitle() {
  const context = useContext(SubtitleContext)
  if (!context) throw new Error('useSubtitle must be used within a SubtitleProvider')
  return context
}
